<template>
  <TemplateBase :page="page">
    <!-- Slot with mobile lyre link component, color is optional in white, default is black, only visible until xl screens, positioning is inside the component -->
    <template #mobileLyre>
      <MoleculeButtonMobileLyre :flush="false" :white-lyre="false" />
    </template>

    <!-- Slot with back button with flush false so it is positioned to the header -->
    <template #backButton>
      <MoleculeButtonBreadcrumbBack :breadcrumbs="breadcrumbs" @click="registerGtmHomeButtonClick" />
    </template>

    <!-- This is where the main part of the template starts, the main tag lives in Base.vue, don't put it in other templates -->

    <!-- Start header: CV13 A = control, B = V1 -->
    <MoleculeABTestWrapper experiment-code="cv13" default-variant="A">
      <template #A>
        <OrganismOverviewHeaderAndFiltersControl
          :active-facets="activeFacets"
          :facets="facets"
          :introduction="t_('concerts-found', { count: totalHits })"
          :introduction-mobile="t_('concerts-found-mobile', { count: totalHits })"
          :keyword-suggestions="keywordSuggestions"
          :keyword="keyword"
          :show-date-picker="true"
          :title="page.title"
          :total-hits="totalHits"
          class="mb-4"
          translation-group="event-overview"
          @reset="reset"
          @set-keyword="setKeyword"
          @toggle-facet="toggleFacet"
        />
      </template>
      <template #B>
        <OrganismOverviewHeaderAndFiltersV1
          :active-facets="activeFacets"
          :facets="facets"
          :introduction="t_('concerts-found', { count: totalHits })"
          :introduction-mobile="t_('concerts-found-mobile', { count: totalHits })"
          :keyword="keyword"
          :show-date-picker="true"
          :title="page.title"
          :total-hits="totalHits"
          class="mb-4"
          suggestion-store-namespace="event-overview"
          translation-group="event-overview"
          @reset="reset"
          @set-keyword="setKeyword"
          @toggle-facet="toggleFacet"
        />
      </template>
    </MoleculeABTestWrapper>
    <!-- End header -->

    <!-- Top results ordered by relevance -->
    <section v-if="topEvents.length > 0" class="container mx-auto v-page-section-container-padding my-6 lg:grid lg:grid-cols-12 gap-x-8">
      <div class="col-span-full xl:col-start-2 xl:col-end-12 2xl:col-start-3 2xl:col-end-11">
        <p class="mb-2 lg:mb-3 text-base font-semibold" v-html="t_('best-results', { keyword })" />
        <MoleculeComponentJoin v-slot="{ item }" :items="topEvents" :separator="' '" class="leading-[1.75]">
          <MoleculeCardResultInline :key="item.url" :url="item.url" :when="item.eventDate" :title="item.title" />
        </MoleculeComponentJoin>
      </div>
    </section>

    <!-- Results -->
    <section class="relative z-0 container mx-auto v-page-section-container-padding">
      <ul v-if="eventsByDay.length" class="lg:grid lg:grid-cols-12 gap-x-8">
        <li v-for="{ date, events } of eventsByDay" :key="date" class="c-event-overview-list-item-day">
          <h3 class="c-event-overview-list-item-day__title">{{ $d(new Date(date), 'short-date') }}</h3>
          <ul class="mb-8">
            <li v-for="event of events" :key="event.iConcertId" class="mb-6">
              <MoleculeCardEventAndSeries
                :concert-status="event.concertStatus"
                :end-date="event.eventEndDate"
                :event-id="event.iConcertId"
                :image="get(event, 'production.teaserImage')"
                :location="get(event, 'room.title')"
                :stream-url="event.streamUrl"
                :title="get(event, 'production.title')"
                :tix-pricing="prepareTixPricing(event.pricing)"
                :tix-sale="prepareTixSale(event)"
                :url="event.url"
                :when="event.eventDate"
                :works="get(event, 'production.works')"
              />
              <div v-if="bannersAtPosition[event.index]" class="my-8">
                <MoleculeCardBanner v-bind="bannerProps(bannersAtPosition[event.index])" />
              </div>
            </li>
          </ul>
        </li>
      </ul>
      <OrganismPaginationV2
        :count="totalHits"
        :limit="hitsPerPage"
        :page="currentPage"
        class="col-span-full mt-1 mb-8 md:mb-16"
        @click="({ page: newPage }) => setCurrentPage(newPage)"
      />
      <div v-if="page.paginationBanner" class="mb-8 lg:grid lg:grid-cols-12">
        <MoleculeCardBanner
          :title="page.paginationBanner.title"
          :top-label="page.paginationBanner.label"
          :sub-label="page.paginationBanner.subtitle"
          :icon="page.paginationBanner.icon ? page.paginationBanner.icon.slug : undefined"
          :image="page.paginationBanner.image"
          :button-title="page.paginationBanner.buttonTitle"
          :button-url="page.paginationBanner.buttonUrl"
          :theme="page.paginationBanner.theme"
          :is-questionnaire-promo="page.paginationBanner.isQuestionnairePromo"
          class="col-start-2 col-end-12"
        />
      </div>
    </section>
  </TemplateBase>
</template>

<script setup lang="ts">
import get from 'lodash/get'

defineProps({
  breadcrumbs: { type: Array as PropType<any[]>, required: true },
  limit: { type: Number, required: false, default: 15 },
  page: { type: Object as PropType<Record<string, any>>, required: true },
})

const route = useRoute()
const router = useRouter()
const eventOverviewStore = useEventOverviewStore()

setupGtmViewItemListWatcher('event-overview/events')
setupGtmFilterSelect()
const registerGtmHomeButtonClick = setupGtmHomeButtonClick()
const { t_ } = setupTranslationPrefixer('EventOverview')
const prepareTixPricing = setupPrepareTixPricing()
const prepareTixSale = setupPrepareTixSale()

const activeFacets = computed(() => eventOverviewStore.activeFacets)
const bannersAtPosition = computed(() => eventOverviewStore.bannersAtPosition)
const currentPage = computed(() => eventOverviewStore.page)
const eventsByDay = computed(() => eventOverviewStore.eventsByDay)
const facets = computed(() => eventOverviewStore.facets)
const hitsPerPage = computed(() => eventOverviewStore.hitsPerPage)
const keyword = computed(() => eventOverviewStore.keyword)
const keywordSuggestions = computed(() => eventOverviewStore.keywordSuggestions)
const topEvents = computed(() => eventOverviewStore.topEvents)
const totalHits = computed(() => eventOverviewStore.totalHits)

/*
 * Maps a CMS banner block onto MoleculeCardBanner's props. A plain function rather than the previous
 * `v-if="banner = bannersAtPosition[...]"` assignment, which created the alias by writing to the
 * render context.
 */
const bannerProps = (banner: CmsData) => ({
  title: banner.title,
  topLabel: banner.label,
  subLabel: banner.subtitle,
  icon: banner.icon ? banner.icon.slug : undefined,
  image: banner.image,
  buttonTitle: banner.buttonTitle,
  buttonUrl: banner.buttonUrl,
  theme: banner.theme,
  isQuestionnairePromo: banner.isQuestionnairePromo,
})

function reset() {
  eventOverviewStore.reset()
}
function setCurrentPage(newPage: number) {
  eventOverviewStore.setPage(newPage)
}
function setUrlParameters(query: CmsData) {
  eventOverviewStore.setUrlParameters(query)
}
function setKeyword(newKeyword: string) {
  eventOverviewStore.setKeyword(newKeyword)
}
function toggleFacet(payload: { name: string; value: string }) {
  eventOverviewStore.toggleFacet(payload)
}

watch(
  () => route.query,
  (query) => {
    if (!routerQueryIsEqual(eventOverviewStore.urlParameters, query)) {
      setUrlParameters(query)
    }
  },
)
watch(
  () => eventOverviewStore.urlParameters,
  (query) => {
    if (!routerQueryIsEqual(route.query, query)) {
      router.push({ query })
    }
  },
)
</script>

<style lang="scss" scoped>
.c-event-overview-list-item-day {
  @apply col-span-full mb-4;

  @screen xl {
    @apply col-start-2 col-end-12;
  }

  @screen 2xl {
    @apply col-start-3 col-end-11;
  }
}

.c-event-overview-list-item-day__title {
  @apply relative flex items-center text-right text-black font-semibold leading-none text-xl mb-4;

  @screen lg {
    @apply mb-8 text-3xl;
  }

  &::before {
    @apply block flex-auto bg-white mr-4;
    content: '';
    height: 2px;
  }
}
</style>
